import { FILE_DELIMITER } from './disbox-file-manager';
import { botJson, fileUrl } from './api.js';

// Ekstensi subtitle yang didukung, diurutkan dari yang paling "aman" dikonversi
// (WebVTT asli, lalu SRT yang formatnya nyaris sama) sampai yang paling rumit
// (ASS/SSA, style & positioning-nya dibuang saat konversi, cuma teksnya yang
// dipakai) - urutan ini juga dipakai untuk menentukan subtitle mana yang
// otomatis dipilih duluan kalau ketemu lebih dari satu.
export const SUBTITLE_EXTENSIONS = ['.vtt', '.srt', '.ass', '.ssa'];

function getExtension(name) {
    const lower = name.toLowerCase();
    return SUBTITLE_EXTENSIONS.find((ext) => lower.endsWith(ext)) || null;
}

// Cari file subtitle terpisah di folder yang sama dengan videonya, yang nama
// filenya diawali nama video (tanpa ekstensi) - contoh "Movie.mkv" cocok
// dengan "Movie.ass" atau "Movie.id.srt". Dipakai supaya subtitle otomatis
// terpasang saat preview video, tanpa harus upload manual tiap kali.
export function findSiblingSubtitles(fileManager, videoPath) {
    const lastSlash = videoPath.lastIndexOf(FILE_DELIMITER);
    const dirPath = lastSlash <= 0 ? '' : videoPath.substring(0, lastSlash);
    const videoName = videoPath.substring(lastSlash + 1);
    const dotIndex = videoName.lastIndexOf('.');
    const videoBase = (dotIndex === -1 ? videoName : videoName.substring(0, dotIndex)).toLowerCase();

    let children;
    try {
        children = fileManager.getChildren(dirPath);
    } catch {
        return [];
    }

    return Object.values(children)
        .filter((child) => {
            if (child.type !== 'file') return false;
            const ext = getExtension(child.name);
            if (!ext) return false;
            const lower = child.name.toLowerCase();
            const subBase = lower.slice(0, lower.length - ext.length);
            return subBase === videoBase || subBase.startsWith(`${videoBase}.`);
        })
        .sort((a, b) => SUBTITLE_EXTENSIONS.indexOf(getExtension(a.name)) - SUBTITLE_EXTENSIONS.indexOf(getExtension(b.name)));
}

// SRT -> WebVTT: formatnya nyaris identik, cuma pemisah milidetik pakai koma
// (harus titik di WebVTT) dan butuh header "WEBVTT" di baris pertama.
function srtToVtt(source) {
    const body = source
        .replace(/\r\n/g, '\n')
        .trim()
        .replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');
    return `WEBVTT\n\n${body}\n`;
}

function parseAssTime(raw) {
    const m = raw.trim().match(/^(\d+):(\d{2}):(\d{2})\.(\d{2})$/);
    if (!m) return null;
    const [, h, mm, ss, cs] = m;
    const hh = String(h).padStart(2, '0');
    const ms = String(Number(cs) * 10).padStart(3, '0');
    return `${hh}:${mm}:${ss}.${ms}`;
}

// Buang override tag ASS seperti "{\an8}" atau "{\i1}" (styling/positioning -
// tidak ada padanannya di WebVTT polos), ubah line-break "\N"/"\n" jadi baris
// baru sungguhan, dan "\h" (hard space) jadi spasi biasa.
// Teks setelah "{\p1}" sampai "{\p0}" adalah gambar vektor (bentuk/tanda),
// bukan kata - dibuang supaya tidak muncul sebagai "m 0 0 l 80 ...".
function stripAssTags(text) {
    let drawing = false;
    let plain = '';
    for (const part of text.split(/(\{[^}]*\})/)) {
        if (part.startsWith('{') && part.endsWith('}')) {
            const modes = part.match(/\\p(\d+)/g);
            if (modes) drawing = Number(modes[modes.length - 1].slice(2)) > 0;
        } else if (!drawing) {
            plain += part;
        }
    }
    return plain
        .replace(/\\N/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\h/g, ' ');
}

// ASS/SSA -> WebVTT: cuma ambil teks dialog & timing-nya dari section
// [Events]. Efek styling/karaoke/posisi ASS memang hilang (WebVTT native
// browser tidak sekaya itu), tapi teks & timing-nya tetap benar dan langsung
// bisa dipakai <video><track> browser tanpa library tambahan.
function assToVtt(source) {
    const lines = source.replace(/\r\n/g, '\n').split('\n');
    let inEvents = false;
    let format = null;
    let startIdx = -1;
    let endIdx = -1;
    let effectIdx = -1;
    const cues = [];
    const seen = new Set();

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (/^\[.*\]$/.test(line)) {
            inEvents = /^\[Events\]$/i.test(line);
            continue;
        }
        if (!inEvents || !line) continue;

        if (/^Format:/i.test(line)) {
            format = line.slice(line.indexOf(':') + 1).split(',').map((s) => s.trim());
            startIdx = format.findIndex((f) => /^start$/i.test(f));
            endIdx = format.findIndex((f) => /^end$/i.test(f));
            effectIdx = format.findIndex((f) => /^effect$/i.test(f));
            continue;
        }
        if (!format || startIdx === -1 || endIdx === -1 || !/^Dialogue:/i.test(line)) continue;

        const rest = line.slice(line.indexOf(':') + 1);
        const parts = rest.split(',');
        if (parts.length < format.length) continue;

        // Field "Text" selalu yang terakhir & boleh mengandung koma - jadi
        // cuma field-field SEBELUM Text yang di-split per koma, sisanya
        // digabung balik jadi satu field Text utuh.
        const fixedCount = format.length - 1;
        const fields = parts.slice(0, fixedCount).map((s) => s.trim());
        const text = parts.slice(fixedCount).join(',');

        const start = parseAssTime(fields[startIdx]);
        const end = parseAssTime(fields[endIdx]);
        if (!start || !end) continue;

        // Baris hasil template karaoke Aegisub (Effect "fx") = animasi per suku kata.
        if (effectIdx !== -1 && /^fx$/i.test(fields[effectIdx])) continue;
        // Baris kosong di tengah cue memutus format VTT.
        const cleanText = stripAssTags(text).split('\n').map((s) => s.trim()).filter(Boolean).join('\n');
        if (!cleanText) continue;
        // Karaoke/efek per huruf: tiap huruf/suku kata baris sendiri dengan
        // posisi tetap. Tanpa posisi, semuanya menumpuk jadi huruf vertikal.
        if (/\\(pos|move)\s*\(/.test(text) && cleanText.replace(/\s/g, '').length <= 3) continue;
        // Lapisan efek sering mengulang baris yang sama persis.
        const key = `${start}|${end}|${cleanText}`;
        if (seen.has(key)) continue;
        seen.add(key);

        cues.push({ start, end, text: cleanText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') });
    }

    cues.sort((a, b) => a.start.localeCompare(b.start));

    let vtt = 'WEBVTT\n\n';
    cues.forEach((cue, i) => {
        vtt += `${i + 1}\n${cue.start} --> ${cue.end}\n${cue.text}\n\n`;
    });
    return vtt;
}

// Konversi isi file subtitle apa pun (dideteksi dari nama filenya) ke WebVTT,
// satu-satunya format yang dipahami native oleh <track> di browser.
export function convertSubtitleToVtt(text, fileName) {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.ass') || lower.endsWith('.ssa')) return assToVtt(text);
    if (lower.endsWith('.srt')) return srtToVtt(text);
    return text;
}

export function isAssFileName(fileName) {
    const lower = String(fileName || '').toLowerCase();
    return lower.endsWith('.ass') || lower.endsWith('.ssa');
}

// ===================== Info media dari bot (ffprobe/ffmpeg) =====================

function encodeVideoPath(videoPath) {
    return videoPath.split(FILE_DELIMITER).filter(Boolean).map(encodeURIComponent).join('/');
}

// Info lengkap file video lewat bot: track video/audio/subtitle tertanam,
// chapter (untuk "Lewati OP"), font yang ditanam di MKV, durasi.
export async function fetchMediaInfo(userId, videoPath) {
    return botJson(`/probe/${userId}/${encodeVideoPath(videoPath)}`, { timeoutMs: 120000 });
}

// Titik mulai SEBENARNYA kalau mode kompatibel dimulai dari detik `seconds`
// (video disalin apa adanya, jadi selalu mulai dari keyframe sebelumnya).
export async function fetchSeekStart(userId, videoPath, seconds) {
    const data = await botJson(`/probe/${userId}/${encodeVideoPath(videoPath)}?seek=${encodeURIComponent(seconds)}`, { timeoutMs: 60000 });
    return Number(data.start) || 0;
}

// Isi satu track subtitle tertanam: format 'vtt' (untuk <track>) atau 'ass'
// (teks ASS asli untuk renderer JASSUB). Bot mengekstrak SEMUA track sekali
// jalan lalu menyimpannya, jadi ganti-ganti track berikutnya instan.
export function embeddedSubtitleUrl(userId, videoPath, trackIndex, format = 'vtt') {
    return fileUrl('subtitles', userId, videoPath, { track: trackIndex, format });
}

export function embeddedFontUrl(userId, videoPath, fontName) {
    return fileUrl('subtitles', userId, videoPath, { font: fontName });
}

export function remuxUrl(userId, videoPath, { audio, start }) {
    return fileUrl('remux', userId, videoPath, { audio, start: start ? start.toFixed(3) : undefined });
}

export function subtitleTrackLabel(track) {
    const lang = track.language ? track.language.toUpperCase() : null;
    const suffix = track.text === false ? ' (gambar, pakai VLC)' : '';
    if (track.title && lang) return `Tersemat: ${track.title} (${lang})${suffix}`;
    if (track.title) return `Tersemat: ${track.title}${suffix}`;
    if (lang) return `Tersemat: ${lang}${suffix}`;
    return `Tersemat: Track ${track.index}${suffix}`;
}

export function audioTrackLabel(track, position) {
    const lang = track.language ? track.language.toUpperCase() : null;
    const channels = track.channels ? ` ${track.channels === 2 ? 'stereo' : track.channels === 1 ? 'mono' : `${track.channels}ch`}` : '';
    const codec = track.codec ? track.codec.toUpperCase() : '';
    const name = track.title || (lang ? `Bahasa ${lang}` : `Audio ${position + 1}`);
    return `${name}${lang && track.title ? ` (${lang})` : ''} - ${codec}${channels}`;
}

const BROWSER_AUDIO = new Set(['aac', 'mp3', 'opus', 'vorbis', 'flac']);

// Apakah video ini sebaiknya diputar lewat "mode kompatibel" (remux di bot)?
// - audio AC3/E-AC3/DTS/TrueHD/PCM: browser tidak bisa memutar (video bisu)
// - video HEVC di browser yang tidak mendukung HEVC
export function compatReason(info) {
    if (!info) return null;
    const audio = (info.audio || []).find((a) => a.isDefault) || (info.audio || [])[0];
    if (audio && audio.codec && !BROWSER_AUDIO.has(audio.codec)) {
        return `Audio ${audio.codec.toUpperCase()} tidak didukung browser`;
    }
    if (info.video && info.video.codec === 'hevc' && !supportsHevc()) {
        return 'Video HEVC tidak didukung browser ini';
    }
    return null;
}

function supportsHevc() {
    try {
        const video = document.createElement('video');
        return Boolean(video.canPlayType('video/mp4; codecs="hvc1.1.6.L93.B0"'));
    } catch {
        return false;
    }
}
